// src/components/Inventory/StockHistoryDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as api from "../../api/index.js";

const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const formatDate = (d) =>
  new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);

const formatTime = (d) =>
  new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true }).format(d);

const typeMeta = (type) => {
  const t = String(type || "").toUpperCase();
  if (t === "RESTOCK") return { label: "Restock", variant: "filled" };
  if (t === "SALE") return { label: "Sale", variant: "filled" };
  if (t === "ADJUSTMENT") return { label: "Adjust", variant: "filled" };
  if (t === "CREATED") return { label: "Created", variant: "filled" };
  return { label: t || "—", variant: "outlined" };
};

const deltaColor = (delta) => {
  const v = safeNum(delta);
  if (v > 0) return "#059669";
  if (v < 0) return "#dc2626";
  return "#6b7280";
};

const REASON_LABELS = {
  POS_SALE: "Sale",
  BATCH_CREATED: "Batch Created",
  STOCK_COUNT: "Stock count correction",
  DATA_ENTRY_MISTAKE: "Data entry correction",
  DAMAGED: "Damaged items",
  EXPIRED: "Expired items",
  THEFT: "Missing / theft",
  OTHER: "Other",
  PURCHASE: "Restocked",

};


const buildPrimary = (m) => {
  const type = String(m.type || "").toUpperCase();
  const mode = String(m.mode || "").toUpperCase();
  const delta = safeNum(m.delta);
  const afterQty = safeNum(m.afterQty);

  if (type === "RESTOCK") return `Restocked +${Math.abs(delta)} units`;
  if (type === "SALE") return `Sold ${Math.abs(delta)} units`;
  if (type === "ADJUSTMENT") {
    if (mode === "SET") return `Stock corrected to ${afterQty}`;
    // DELTA / unknown
    const sign = delta > 0 ? "+" : "";
    return `Stock corrected ${sign}${delta} units`;
  }
  if (type === "CREATED") return `Batch created with ${afterQty} units`;

  const sign = delta > 0 ? "+" : "";
  return `Stock change ${sign}${delta} units`;
};

const buildSecondary = (m) => {
  const beforeQty = safeNum(m.beforeQty);
  const afterQty = safeNum(m.afterQty);
  const reasonCode = (m.reason && String(m.reason).trim()) || "";
  const reason = REASON_LABELS[reasonCode] || (reasonCode || "—");

  const refNo = (m.refNo && String(m.refNo).trim()) || "—";
  const createdAt = m.createdAt ? new Date(m.createdAt) : null;

  const datePart = createdAt ? formatDate(createdAt) : "—";
  const timePart = createdAt ? formatTime(createdAt) : "—";

  return `Before ${beforeQty} → After ${afterQty} • Reason: ${reason} • Ref: ${refNo} • ${datePart} • ${timePart}`;
};

export default function StockHistoryDialog({ open, onClose, row }) {
  const inventoryId = row?.id || row?._id;

  const title =
    (row?.medicineName ? row.medicineName : "Stock History") +
    (row?.batchNumber ? ` — Batch ${row.batchNumber}` : "");

  const subtitle = row?.genericName ? row.genericName : "";

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open || !inventoryId) return;

    let alive = true;
    setLoading(true);
    setErr("");

    api
      .fetchStockMovements(inventoryId, 200)
      .then(({ data }) => {
        if (!alive) return;
        setItems(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.response?.data?.message || e?.message || "Failed to load history");
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open, inventoryId]);

  const rows = useMemo(() => {
    return (items || []).map((m) => {
      const delta = safeNum(m.delta);
      const meta = typeMeta(m.type);
      return {
        id: m._id,
        primary: buildPrimary(m),
        secondary: buildSecondary(m),
        note: (m.note && String(m.note).trim()) || "",
        chip: meta,
        delta,
      };
    });
  }, [items]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6, pb: 1.2 }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.2 }}>
            {subtitle}
          </Typography>
        ) : null}

        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            border: "1px solid #e5e7eb",
            borderRadius: 2,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
            <CircularProgress size={22} />
            <Typography sx={{ color: "#374151" }}>Loading history…</Typography>
          </Box>
        ) : err ? (
          <Typography sx={{ color: "#dc2626", fontWeight: 800 }}>{err}</Typography>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900, color: "#111827" }}>
              No history yet
            </Typography>
            <Typography sx={{ color: "#6b7280", mt: 0.5 }}>
              Restocks, sales, and adjustments will appear here.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {rows.map((r, idx) => {
              const isLast = idx === rows.length - 1;
              const dColor = deltaColor(r.delta);

              return (
                <Box key={r.id} sx={{ display: "flex", gap: 2, py: 1.5 }}>
                  {/* left timeline rail */}
                  <Box sx={{ width: 18, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: dColor,
                        mt: 0.6,
                      }}
                    />
                    {!isLast && (
                      <Box sx={{ width: 2, flex: 1, background: "#e5e7eb", mt: 0.8 }} />
                    )}
                  </Box>

                  {/* content card-ish */}
                  <Box
                    sx={{
                      flex: 1,
                      border: "1px solid #eef2f7",
                      borderRadius: 3,
                      px: 2,
                      py: 1.4,
                      background: "#fff",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip size="small" label={r.chip.label} variant={r.chip.variant} />
                      <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                        {r.primary}
                      </Typography>

                      <Box sx={{ ml: "auto" }}>
                        <Typography sx={{ fontWeight: 900, color: dColor }}>
                          {r.delta > 0 ? "+" : ""}
                          {safeNum(r.delta)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        mt: 0.6,
                        lineHeight: 1.4,
                      }}
                    >
                      {r.secondary}
                    </Typography>

                    {r.note ? (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.8,
                          color: "#111827",
                          background: "#f9fafb",
                          border: "1px solid #eef2f7",
                          borderRadius: 2,
                          px: 1.2,
                          py: 0.8,
                        }}
                      >
                        <span style={{ fontWeight: 800 }}>Note:</span> {r.note}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
